import Box from '@mui/material/Box'
import React, { useCallback, useMemo, useRef } from 'react'
import { Outlet, useLocation } from 'react-router'
import Footer from '../components/Footer'
import BottomAppBar from '../components/navBar/BottomAppBar'
import NavBar from '../components/navBar/NavBar'
import ScrollToTop from '../components/ScrollToTop'
import { WebLayoutProvider } from '../context/WebLayoutContext'
import { usePlannerMode } from '../context/PlannerModeContext'
import { PLANNER_BAR_HEIGHT_PX } from '../components/planner/PlannerModeBar'
import { ROUTES } from '../helpers/routes-helper'
import { useDevice } from '../hooks/useDevice'
import { Session } from '@talkjs/react'
import { useAppSelector } from '../store'
import { selectEater } from '../store/profileSlice'
import Talk from 'talkjs'
import {
  DEFAULT_AVATAR_IMAGE,
  toTalkPhotoUrl,
} from '../helpers/static-constants'
import { useAuth0 } from '@auth0/auth0-react'
import { selectChatToken } from '../store/restaurant/messages'
import ContentCreatorBanner from '../components/banner/ContentCreatorBanner'
import { selectIsContentCreatorBannerShow } from '../store/influencer/contentCreator'

const WebMobileLayout: React.FC = () => {
  const location = useLocation()
  const { isHybrid } = useDevice()
  // Undefined outside the planner provider (tests, isolated harnesses) — then there is no bar to
  // clear and the layout is unchanged.
  const isPlannerActive = usePlannerMode()?.active ?? false
  const { isAuthenticated } = useAuth0()
  const eater = useAppSelector(selectEater)
  const chatToken = useAppSelector(selectChatToken)
  const isContentCreatorBannerShow = useAppSelector(
    selectIsContentCreatorBannerShow,
  )
  const scrollableContainerRef = useRef<HTMLDivElement>(null)

  const hideFooterPaths = useMemo(
    () => [ROUTES.HOME, ROUTES.ABOUT, ROUTES.POINTS],
    [],
  )
  const showFooter = useMemo(
    () => hideFooterPaths.includes(location.pathname),
    [hideFooterPaths, location.pathname],
  )
  const hideNavBar = useMemo(
    () =>
      isHybrid &&
      (
        location.pathname === ROUTES.PROFILE ||
        location.pathname === ROUTES.MENU_SCAN_RESULTS ||
        location.pathname.startsWith(`${ROUTES.SAVED_MENU_DETAIL}/`)
      ),
    [isHybrid, location.pathname],
  )

  const syncUser = useCallback(
    () => {
      const photoUrl = toTalkPhotoUrl(eater?.imageUrl, DEFAULT_AVATAR_IMAGE)
      return new Talk.User({
        id: eater?.eaterId ?? '',
        name: `${eater?.firstName ?? ''} ${eater?.lastName ?? ''}`,
        email: eater?.email !== '' ? (eater?.email ?? null) : null,
        ...(photoUrl ? { photoUrl } : {}),
        role: 'eater-restaurant-inbox',
        custom: {
          email: eater?.email !== '' ? (eater?.email ?? null) : null,
        },
      })
    },
    [eater],
  )

  // TODO: add chat token fetching
  // const fetchToken = async (): Promise<string> => {
  //   try {
  //     const res = await dispatch(fetchChatToken())
  //     if (fetchChatToken.fulfilled.match(res)) {
  //       return res.payload as string
  //     }
  //     return ''
  //   } catch (error) {
  //     return ''
  //   }
  // }

  const layoutContent = (
    <Box
      display="flex"
      flexDirection="column"
      height="100vh"
      sx={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {isContentCreatorBannerShow && <ContentCreatorBanner />}
      {!hideNavBar && <NavBar />}
      <WebLayoutProvider>
        <Box
          ref={scrollableContainerRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            scrollBehavior: 'smooth',
            display: 'flex',
            flexDirection: 'column',
            // The planner bar is position:fixed and contributes nothing to layout, so without this
            // it covers the bottom of every page and the content under it cannot be scrolled to.
            // This IS the scroll container (the body does not scroll), so the padding belongs here.
            ...(isPlannerActive
              ? { paddingBottom: `${PLANNER_BAR_HEIGHT_PX}px` }
              : {}),
          }}
        >
          <Outlet context={{ scrollableContainerRef }} />
          <ScrollToTop scrollableContainerRef={scrollableContainerRef} />
          {showFooter && !isHybrid && <Footer />}
        </Box>
      </WebLayoutProvider>
      {isHybrid && <BottomAppBar />}
    </Box>
  )

  return (
    <>
      {isAuthenticated && eater?.eaterId && chatToken ? (
        <Session
          appId={process.env.REACT_APP_TALK_JS ?? ''}
          syncUser={syncUser}
          token={chatToken}
          // tokenFetcher={fetchToken}
        >
          {layoutContent}
        </Session>
      ) : (
        layoutContent
      )}
    </>
  )
}

export default React.memo(WebMobileLayout)
